import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { BsModalRef } from "ngx-bootstrap/modal";
import { CommonHttpService } from "src/app/shared/services/common-http/common-http.service";
import { devicePixelRatio, linkPattern } from "src/app/shared/constants";
import { ActivatedRoute } from "@angular/router";
import { Resource } from "src/app/shared/model/resource-model";
import {
  resourceAllowedExtension,
  videoAllowedExtension,
  imageAllowedExtension,
} from "src/app/shared/constants";
import { includes } from "lodash";
import { MessageService } from "src/app/shared/services/message/message.service";
import { Subject } from "rxjs";
import { UtilitiesService } from "src/app/shared/services/utilities/utilities.service";

@Component({
    selector: "app-editresource",
    templateUrl: "./editresource.component.html",
    styleUrls: ["./editresource.component.scss"],
    standalone: false
})
export class EditresourceComponent implements OnInit {
  companyId: number;
  resourceData: any;
  containerId: number;
  resourceCount: number;
  isUpdate: boolean;
  profileId: number;
  imageName: any;
  uploadResourceForm: UntypedFormGroup;
  selectedFile: any;
  playbookId: number;
  languageId: number;
  dataSubmitted = false;
  errMessage: string;
  pageid: number;
  translationResource: any;
  progressbarDisplay = {
    image: false,
    asset: false,
  };
  dynamicProgressbar = {
    image: null,
    asset: null,
  };
  max = 100;
  videoProcessing: string;
  resourceAllowedExtensions = resourceAllowedExtension;
  videoAllowedExtensions = videoAllowedExtension;
  imageAllowedExtensions = imageAllowedExtension;
  devicePixelRatio = devicePixelRatio;
  updateResourceContainer = new Subject();
  apiUrl = "/api/Assets/UploadVideoFileAsChunk";
  @ViewChild("fileInputResource") fileInputResource: ElementRef;
  @ViewChild("fileInputPlaceHolder") fileInputPlaceHolder: ElementRef;
  constructor(
    private utilitiesService: UtilitiesService,
    private formBuilder: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private messageService: MessageService,
    private modalRef: BsModalRef,
    private commonHttpService: CommonHttpService
  ) {}

  ngOnInit() {
    this.languageId = this.activatedRoute.snapshot.queryParams.languageId || 1;
    if (!this.resourceData) {
      this.resourceData = new Resource();
      this.resourceData.type = 0;
    }
    this.selectedFile = null;
    this.imageName = null;
    this.dataSubmitted = false;
    this.languageId = this.translationResource.languageId;
    this.assignNull();
    if (this.isUpdate) {
      this.getEachResource(this.translationResource);
    } else {
      this.createForm();
    }
  }

  getEachResource(translationResource) {
    this.commonHttpService
      .get({
        uri: `/api/Translation/GetResourceForTranslation?playbookId=${this.playbookId}&companyId=${this.companyId}&languageId=${this.languageId}&resourceId=${translationResource.resourceId}&refResourceId=${translationResource.refResourceId}`,
      })
      .subscribe((responseData) => {
        this.resourceData = responseData;
        this.createForm();
        this.changeValidation();
      });
  }
  get form() {
    return this.uploadResourceForm.controls;
  }

  /**
   * To clear the file input resource
   */
  clearResource() {
    if (this.fileInputResource) {
      this.fileInputResource.nativeElement.value = null;
    }
  }

  /**
   * To clear the uploaded placeholder image
   */
  clearPlaceHolder() {
    if (this.fileInputPlaceHolder) {
      this.fileInputPlaceHolder.nativeElement.value = null;
    }
  }

  /**
   * To initialize the resource form
   */
  createForm() {
    this.uploadResourceForm = this.formBuilder.group({
      title: [
        this.resourceData.title || null,
        [Validators.required, Validators.maxLength(100)],
      ],
      linkUrl: [
        this.resourceData.linkUrl || null,
        [Validators.pattern(linkPattern)],
      ],
    });
  }

  /**
   * To close the resource modal
   */
  closeModal() {
    this.modalRef.hide();
  }

  /**
   * To manage validation conditions based on different resource types
   */
  changeValidation() {
    const linkUrl = this.uploadResourceForm.get("linkUrl");
    if (this.resourceData.type === 1) {
      linkUrl.setValidators([
        Validators.required,
        Validators.pattern(linkPattern),
      ]);
    } else {
      linkUrl.clearValidators();
    }
    linkUrl.updateValueAndValidity();
  }

  /**
   * To handle switching of resource types
   * @param resourceType changed resource type value
   */
  changeResource(resourceType) {
    this.resourceData.type = resourceType;
    this.changeValidation();
    this.assignNull();
  }

  /**
   * To assign null values to resource fields when changing resource types
   */
  assignNull() {
    this.progressbarDisplay = {
      image: false,
      asset: false,
    };
    this.resourceData.asset = null;
    this.resourceData.assetId = null;
    this.resourceData.placeHolderImageKey = null;
  }

  /**
   * To validate extension of uploaded file
   * @param extensionAllowedArray allowed extension values
   */
  checkExtensionFunction(extensionAllowedArray) {
    const extensionArray = this.selectedFile.name.split(".");
    const extension = extensionArray[extensionArray.length - 1].toLowerCase();
    const checkExtension = includes(extensionAllowedArray, extension);
    return checkExtension;
  }

  /**
   * To handle file change event for file and images
   * @param event $event
   * @param extensionAllowedArray allowed extension values
   * @param placeHolder flag for placeholder image
   */
  fileChangeEvent(
    event: any,
    extensionAllowedArray: Array<any>,
    placeHolder?: boolean
  ): void {
    placeHolder = placeHolder ? placeHolder : null;
    this.resourceData.placeHolderImageKey = null;
    if (event.target.files[0] !== undefined) {
      this.selectedFile = event.target.files[0];
      this.imageName = event.target.files[0].name;
      const checkExtension = this.checkExtensionFunction(extensionAllowedArray);
      if (checkExtension) {
        this.upload(placeHolder);
      } else {
        const isAre = extensionAllowedArray.length > 1 ? "are" : "is";
        const extensionErrorMessage =
          "This is not a valid extension. Supported extension(s) " +
          isAre +
          " : " +
          extensionAllowedArray.join(",") +
          ".";
        this.messageService.showErrorMessage(extensionErrorMessage);
      }
    }
  }

  /**
   * To handle upload of file or images
   * @param placeHolder flag for placeholder image
   */
  upload(placeHolder) {
    const formData: FormData = new FormData();
    formData.append("file", this.selectedFile);
    this.progressbarDisplay.image = true;
    this.dynamicProgressbar.image = 0;
    if (!placeHolder) {
      this.resourceData.asset = null;
    }
    this.commonHttpService
      .postWithProgress({
        uri: "/api/Assets/UploadResource",
        object: formData,
      })
      .subscribe((response: any) => {
        if (typeof response === "number") {
          // To display progress bar while file is being uploaded
          this.dynamicProgressbar.image = response;
        } else if (response) {
          this.progressbarDisplay.image = false;
          if (placeHolder) {
            this.resourceData.placeHolderImageKey = response.id;
          } else {
            this.resourceData.placeHolderImageKey = this.resourceData
              .placeHolderImageKey
              ? this.resourceData.placeHolderImageKey
              : null;
            this.resourceData.asset = response;
            this.resourceData.assetId = response.id;
            this.resourceData.uRL =
              "/api/Assets/GetResourceById/" + response.id;
          }
        }
      });
  }

  progressPercentage(progress) {
    this.progressbarDisplay.asset = true;
    this.resourceData.asset = null;
    if (typeof progress.percentage === "number") {
      this.dynamicProgressbar.asset = progress.percentage;
      this.videoProcessing = progress.text;
    } else {
      this.dynamicProgressbar.asset = 100;
      this.videoProcessing = progress.percentage;
    }
  }

  fileStatusUpdate(fileStatus) {
    this.progressbarDisplay.asset = false;
    if (fileStatus.status) {
      this.resourceData.asset = fileStatus.assetData;
      this.resourceData.assetId = fileStatus.assetData.id;
      this.resourceData.uRL =
        "/api/Assets/GetResourceById/" + fileStatus.assetData.id;
    }
  }

  /**
   * To remove uploaded placeholder image
   */
  removePlaceHolderImageKey() {
    this.resourceData.placeHolderImageKey = null;
  }

  /**
   * To check required conditions before saving or updating data
   */
  validateData() {
    this.dataSubmitted = true;
    if (
      this.uploadResourceForm.valid &&
      this.resourceData.type != null &&
      ((this.resourceData.type !== 1 && this.resourceData.assetId) ||
        this.resourceData.type === 1)
    ) {
      this.saveData();
    }
    this.checkError();
  }

  /**
   * To display error when required file is not uploaded
   */
  checkError() {
    if (!this.resourceData.assetId && this.resourceData.type !== 1) {
      this.errMessage = "File Upload is required to add new Resource";
    }
  }

  /**
   * To save or update data
   */
  saveData() {
    // To handle resource order
    const resOrder =
      this.resourceData.orderBy || this.resourceData.orderBy === 0
        ? this.resourceData.orderBy
        : this.resourceCount;

    const resObj = {
      containerId: this.pageid,
      type: this.resourceData.type,
      title: this.uploadResourceForm.value.title,
      assetId: this.resourceData.assetId,
      placeHolderImageKey: this.resourceData.placeHolderImageKey,
      linkUrl: this.uploadResourceForm.value.linkUrl,
      orderBy: resOrder,
      profileId: this.profileId,
      playbookId: this.playbookId,
      languageId: this.languageId,
    };
    // To update existig resource
    const updateResObj = {
      ...resObj,
      id: this.resourceData.id || this.translationResource.groupId,
      languageId: this.languageId,
    };
    this.commonHttpService
      .put({
        uri: "/api/Resources/UpdateResource",
        object: updateResObj,
      })
      .subscribe((response) => {
        this.updateResourceContainer.next(response);
        this.closeModal();
        this.messageService.showSuccessMessage(
          "Resource updated successfully."
        );
      });
  }
  getAssetAzureUrl(assetUrl) {
    this.utilitiesService.getAssetAzureUrl(assetUrl);
  }
}
